/**
 * 익스텐션 ↔ 웹 cross-context 메시지 브리지 헬퍼.
 *
 * 의존성 zero 로 유지할 것 — `auth-callback.js` 같은 무거운 모듈에서 빼낸
 * 이유는 `Login/index.js` 같은 곳에서 import 했을 때 redux store / app
 * bootstrap (src/index.js) 까지 import 체인에 끌려와 jest.mock TDZ 사고
 * 등을 일으키기 때문. 새 helper 추가 시에도 redux / api / i18n 같은
 * 모듈 import 를 추가하지 말 것.
 */

/**
 * 로그인 직후 익스텐션이 즉시 로그인 상태를 인식하도록 postMessage 발송.
 * 익스텐션 content-script 가 listener 를 걸어두고 받으면 (1) userLoggedOut
 * 플래그 클리어, (2) 토큰을 chrome.storage 로 sync 한다. 익스텐션이 폴링
 * (~2초) 으로 감지하기 전에 즉시 트리거하기 위함. 자세한 설계는
 * extension/CLAUDE.md 와 frontend/CLAUDE.md 의 EXT_LOGIN bridge 섹션 참조.
 */
export function notifyExtensionLogin() {
  try {
    window.postMessage({ type: 'safebox/EXT_LOGIN' }, window.location.origin)
  } catch (e) {
    // best effort — 익스텐션 미설치 / 장애 시 silent.
  }
}

/**
 * 로그인 시 입력한 마스터 비밀번호를 익스텐션으로 전달. 익스텐션이 받아
 * AES 암호화해 chrome.storage 에 저장하면 옵션 화면에서 마스터 비밀번호를
 * 다시 입력하지 않아도 된다. EXT_LOGIN 직후 호출해야 익스텐션 측 토큰
 * 동기화가 먼저 반영돼 평문이 아닌 암호화 상태로 저장된다.
 */
export function notifyExtensionMasterPassword(password) {
  if (typeof password !== 'string' || !password) return
  try {
    window.postMessage({ type: 'safebox/MASTER_PASSWORD_SYNC', password }, window.location.origin)
  } catch (e) {
    // best effort — 익스텐션 미설치 / 장애 시 silent.
  }
}

/**
 * 클립보드 자동삭제 유지시간 설정 변경을 익스텐션에 즉시 전달. 익스텐션이 받으면
 * chrome.storage 캐시를 갱신해 다음 복사부터 새 값을 적용한다. 익스텐션은 자체적으로
 * GET /v1/user-settings 도 주기 fetch 하므로, 이 신호는 "즉시 갱신" 보조 경로다.
 * content: '0'=Never(off), '1'~'5'=분.
 */
export function notifyExtensionClipboardSetting(content) {
  if (typeof content !== 'string') return
  try {
    window.postMessage({ type: 'safebox/EXT_CLIPBOARD_SETTING', content }, window.location.origin)
  } catch (e) {
    // best effort — 익스텐션 미설치 / 장애 시 silent.
  }
}

/**
 * 웹에서 비밀번호를 복사한 직후 익스텐션에 "유지시간 뒤 클립보드를 비워달라"고 알린다.
 * 웹 자체 setTimeout 은 우리 웹앱 탭이 포커스일 때만 비울 수 있어, 사용자가 다른 탭/앱으로
 * 이동하면 못 지운다. 익스텐션 background 알람은 포커스된 아무 탭(content script: <all_urls>)
 * 에서 비울 수 있어 커버리지가 넓다. 웹 setTimeout 과 병행(둘 다 빈 문자열 쓰기라 무해).
 * content: '1'~'5'=분. Never(0)는 애초에 호출하지 않는다(스케줄 자체가 없으므로).
 */
export function notifyExtensionClipboardCopy(content) {
  if (typeof content !== 'string') return
  try {
    window.postMessage({ type: 'safebox/EXT_CLIPBOARD_COPIED', content }, window.location.origin)
  } catch (e) {
    // best effort — 익스텐션 미설치 / 장애 시 silent.
  }
}
